import { createContext } from 'react';
import type { z } from 'zod';
import type {
  ICartItemSchema,
  ICartTotalsSchema,
  IStoreCartLineItemSchema
} from '@r/types/zod-schemas.ts';

type CartItem = z.infer<typeof ICartItemSchema>;
type CartTotal = z.infer<typeof ICartTotalsSchema>;

type CartOperationResult = {
  success: boolean;
  error?: string;
};

type CartContextType = {
  cartItems: CartItem[];
  cartWithTotals: { items: CartItem[]; totals: CartTotal };
  cartItemCount: number;
  addItemToCart: (item: CartItem) => Promise<CartOperationResult>;
  updateCartItemQuantity: ({
    cartItem,
    quantity
  }: {
    cartItem: z.infer<typeof IStoreCartLineItemSchema>;
    quantity: number;
  }) => Promise<CartOperationResult>;
  removeCartItem: ({
    cartItem
  }: {
    cartItem: z.infer<typeof IStoreCartLineItemSchema>;
  }) => Promise<CartOperationResult>;
  clearCart: () => Promise<CartOperationResult>;
};

const CartContext = createContext<CartContextType>({
  cartItems: [],
  cartWithTotals: {
    items: [],
    totals: {
      total: null,
      subtotal: null,
      tax_total: null,
      shipping_total: null,
      discount_total: null,
      gift_card_total: null,
      currency_code: '',
      shipping_subtotal: null
    }
  },
  cartItemCount: 0,
  addItemToCart: async () => ({ success: false }),
  updateCartItemQuantity: async () => ({ success: false }),
  removeCartItem: async () => ({ success: false }),
  clearCart: async () => ({ success: false })
});

export { CartContext };
